import { ApiResult, request, safeRequest, successResult } from '@synergy/api';
import { Canceler } from 'axios';
import { DocumentType, Media, UploadType } from '@synergy/models';
import { getUrlSegment } from '@synergy/utils';

export class MediaApi {
    constructor(protected baseUrl: string) {}

    public addFile(
        documentType: DocumentType,
        documentId: number,
        file: File,
        type: UploadType,
        cancelFunction?: (canceler: Canceler) => void,
        progressCallback?: (progress: ProgressEvent) => void
    ): Promise<ApiResult<Media>> {
        const data = new FormData();
        data.append('files[0]', file, file.name);
        data.append('type', type);

        const config = {
            headers: { 'content-type': 'multipart/form-data' },
            onUploadProgress: progressCallback,
            cancelToken: cancelFunction && new request.CancelToken(cancelFunction),
        };

        return safeRequest<Media, Media[]>(
            () => request.post(`${this.baseUrl}/${getUrlSegment(documentType)}/${documentId}/media`, data, config),
            response => Media.fromPlainObject(response.data[0])
        );
    }

    public addFiles(
        documentType: DocumentType,
        documentId: number,
        files: File[],
        type: UploadType
    ): Promise<ApiResult<Media[]>> {
        if (files.length === 0) {
            return Promise.resolve(successResult([]));
        }

        const config = { headers: { 'content-type': 'multipart/form-data' } };
        const data = new FormData();
        files.forEach((file, index) => data.append(`files[${index}]`, file, file.name));
        data.append('type', type);

        return safeRequest(
            () => request.post(`${this.baseUrl}/${getUrlSegment(documentType)}/${documentId}/media`, data, config),
            response => response.data.map(Media.fromPlainObject)
        );
    }

    public updateTitle(fileId: number, title: string | null): Promise<ApiResult<Media>> {
        return safeRequest(
            () => request.patch(`${this.baseUrl}/media/${fileId}/title`, { title }),
            response => Media.fromPlainObject(response.data)
        );
    }

    public removeFile(fileId: number): Promise<ApiResult<null>> {
        return safeRequest(
            () => request.delete(`${this.baseUrl}/media/${fileId}`),
            () => null
        );
    }
}
